import React, { useState, useEffect } from "react";
import { Calendar, Clock, MapPin, Users, Star, ArrowLeft } from "lucide-react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const RentalFieldDetails = () => {
  const { id } = useParams();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRentalField = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/fields-rental/field-by-id/${id}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch rental field details");
        }

        const data = await response.json();
        setRental(data.rental);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRentalField();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="text-center mt-4 text-gray-600">
            Loading field details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Error Loading Field
          </h2>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => window.history.back()}
            className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <div className="text-gray-400 text-6xl mb-4">🏟️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Field Not Found
          </h2>
          <p className="text-gray-600">
            The requested field could not be found.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Fields
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Field Details</h1>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Field Image */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="relative h-96">
              <img
                src={rental.imageUrl || "/api/placeholder/600/400"}
                alt={rental.fieldName}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "/api/placeholder/600/400";
                }}
              />
            </div>
          </div>

          {/* Field Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {rental.fieldName}
              </h2>

              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <span className="text-3xl font-bold text-green-600">
                    ₹{rental.price}
                  </span>
                  <span className="text-gray-600 ml-2">/ {rental.time}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RentalFieldDetails;
